import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useNotification } from "~hooks/useNotification";
import type { DefaultOperationSuccessResponse } from "~types/response";
import type { StandingConfiguration } from "~types/standingConfiguration";

export const useBuildStandings = () => {
  const queryClient = useQueryClient();
  const request = useApi();
  const { sport, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  return useMutation<DefaultOperationSuccessResponse, Error, StandingConfiguration | undefined>({
    mutationFn: async (standingConfiguration?: StandingConfiguration) => {
      if (!standingConfiguration?.standingConfigurationId) {
        notification.error({
          message: formatMessage({ id: "standings.build.error.standingsId" }),
          placement: "bottomRight",
        });
        return { data: [], links: { self: "" } };
      }
      await request(`${sport}/o/${organizationId}/seasons/${seasonId}/standings?forceDelete=true`, {
        method: "DELETE",
      });
      return await request(`${sport}/o/${organizationId}/seasons/${seasonId}/standings/build`, { method: "POST" });
    },
    onSuccess: async (response: DefaultOperationSuccessResponse) => {
      if (response?.data && response.data.length > 0 && response.data[0]?.success) {
        await queryClient.invalidateQueries({
          queryKey: ["standing-configuration", sport, organizationId, seasonId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["standings", sport, organizationId, seasonId],
          exact: false,
        });
        notification.success({
          message: formatMessage({ id: "standings.build.success" }),
          placement: "bottomRight",
        });
      }
    },
  });
};
